import { useUiText } from "../../locale/UiLocale";
import { useEffect, useRef, useState } from "react";
import { ContextMenu, type ContextMenuAnchor, type ContextMenuItem } from "../ContextMenu/ContextMenu";

type Field = HTMLInputElement | HTMLTextAreaElement | HTMLElement;

const EDITABLE = "input, textarea, [contenteditable=''], [contenteditable='true']";
/** Input types that hold text (a checkbox or a color input has nothing to cut or paste). */
const TEXT_TYPES = new Set(["text", "search", "email", "url", "tel", "password", "number", ""]);

function isTextField(el: Element): el is Field {
  if (el instanceof HTMLInputElement) return TEXT_TYPES.has(el.type);
  return el instanceof HTMLTextAreaElement || (el instanceof HTMLElement && el.isContentEditable);
}

function selectedText(field: Field): string {
  if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
    try {
      return field.value.slice(field.selectionStart ?? 0, field.selectionEnd ?? 0);
    } catch {
      // Inputs like email or number have no selection API.
      return "";
    }
  }
  return window.getSelection()?.toString() ?? "";
}

function isReadOnly(field: Field): boolean {
  if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) return field.readOnly || field.disabled;
  return !field.isContentEditable;
}

/**
 * The suite's right-click menu for text fields (Cut, Copy, Paste, Select all), in place of the
 * browser's. Render it once at the app's root. Edits go through the browser's editing commands, so
 * the field's change events fire as if the person had typed.
 */
export function TextFieldMenu(): React.JSX.Element {
  const t = useUiText();
  const [state, setState] = useState<(ContextMenuAnchor & { field: Field; selection: string }) | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    function onContextMenu(e: MouseEvent): void {
      const el = e.target instanceof Element ? e.target.closest(EDITABLE) : null;
      if (!el || !isTextField(el)) return;
      e.preventDefault();
      seq.current += 1;
      const fromKeyboard = e.button !== 2 && e.clientX === 0 && e.clientY === 0;
      const r = el.getBoundingClientRect();
      setState({
        field: el,
        selection: selectedText(el),
        point: fromKeyboard ? { x: r.left, y: r.bottom } : { x: e.clientX, y: e.clientY },
        rect: fromKeyboard ? { left: r.left, top: r.top, right: r.right, bottom: r.bottom } : null,
        returnFocus: el,
        openId: seq.current,
      });
    }
    document.addEventListener("contextmenu", onContextMenu);
    return () => document.removeEventListener("contextmenu", onContextMenu);
  }, []);

  const field = state?.field;
  const hasSelection = Boolean(state?.selection);
  const readOnly = field ? isReadOnly(field) : true;
  const apple = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);
  const key = (k: string) => (apple ? `⌘${k}` : `Ctrl+${k}`);

  /** Runs an editing command on the field (focused again, with its selection intact). */
  function run(command: string, value?: string): void {
    if (!field) return;
    field.focus();
    document.execCommand(command, false, value);
  }

  const items: ContextMenuItem[] = [
    { label: t("Cut"), hint: key("X"), disabled: !hasSelection || readOnly, onSelect: () => run("cut") },
    { label: t("Copy"), hint: key("C"), disabled: !hasSelection, onSelect: () => run("copy") },
    {
      label: t("Paste"),
      hint: key("V"),
      disabled: readOnly || typeof navigator === "undefined" || !navigator.clipboard?.readText,
      onSelect: () => {
        void navigator.clipboard
          .readText()
          .then((text) => run("insertText", text))
          .catch(() => undefined);
      },
    },
    "separator",
    { label: t("Select all"), hint: key("A"), onSelect: () => run("selectAll") },
  ];

  return <ContextMenu state={state} items={items} onClose={() => setState(null)} ariaLabel={t("Edit")} />;
}
